import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PluginService } from '../../../../core/services/plugin.service';
import { FeedService } from '../../../../core/services/wrench-services/feed.service';
import { FmvFeedType, FmvFeedWizard } from '../../../../core/services/wrench-services/fmv-feed.service';

import { FeedWizard } from '../../../../core/interfaces/feed.model';

interface FeedPlugin {
  id: string;
  name: string;
  icon: string;
  getWizard(): any;
}

function isFeedPlugin(obj: any): obj is { getId: () => string; getName: () => string; getIcon: () => string; getWizard: () => any; } {
  return (
    obj &&
    typeof obj.getId === 'function' &&
    typeof obj.getName === 'function' &&
    typeof obj.getIcon === 'function' &&
    typeof obj.getWizard === 'function'
  );
}

@Component({
  selector: 'app-feed-wizard',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './feed-wizard.component.html',
  styleUrl: './feed-wizard.component.css'
})
export class FeedWizardComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() feedCreated = new EventEmitter<any>();

  feedTypes: FeedPlugin[] = [];
  wizardSteps: string[] = [];
  currentStep = 0;
  feedName = 'New Feed';
  autoStartStatus = false;
  showAddView = true;
  feedWizard: FeedWizard & { setFeedService(feedService: { addFeed: (feed: any) => void }): void; };
  wizardForm: FormGroup;
  // Add FMV options for the FMV wizard step
  fmvOptions = { ipaddr: '', videoType: '' };
  videoTypes: string[] = ['video/mp4', 'video/webm', 'video/ogg', 'video/mjpeg'];

  constructor(
    private fb: FormBuilder,
    private pluginService: PluginService,
    private feedService: FeedService
  ) {
    this.wizardForm = this.fb.group({
      feedName: ['New Feed', Validators.required]
    });
    this.feedWizard = new FmvFeedWizard();
    this.feedWizard.setFeedService(this.feedService); // <-- This line is required!
  }

  ngOnInit(): void {
    this.loadFeedTypes();
  }

  async loadFeedTypes(): Promise<void> {
    try {
      const plugins = await this.pluginService.getFeedPlugins();
      // Only add plugins that conform to FeedPlugin interface
      for (const plugin of plugins) {
        if (isFeedPlugin(plugin)) {
          const feedPlugin: FeedPlugin = {
            id: plugin.getId(),
            name: plugin.getName(),
            icon: plugin.getIcon(),
            getWizard: () => plugin.getWizard()
          };
          this.feedTypes.push(feedPlugin);
        }
      }
      // Add core feed types (FMV)
      const fmvFeedType = new FmvFeedType();
      const fmvFeedPlugin: FeedPlugin = {
        id: fmvFeedType.getId(),
        name: fmvFeedType.getName(),
        icon: fmvFeedType.getIcon(),
        getWizard: () => fmvFeedType.getWizard()
      };
      this.feedTypes.push(fmvFeedPlugin);
    } catch (error) {
      console.error('Failed to load feed types:', error);
    }
  }

  feedTypeClicked(feed: FeedPlugin): void {
    this.showAddView = false;
    this.initializeWizard(feed);
  }

  initializeWizard(feed: FeedPlugin): void {
    this.feedWizard = feed.getWizard();
    this.feedWizard.setFeedService(this.feedService);
    this.wizardSteps = [];
    this.wizardSteps.push('Feed Name');
    const pluginSteps = this.feedWizard.getPluginSteps();
    for (const step of pluginSteps) {
      this.wizardSteps.push(step.name);
    }
    this.wizardSteps.push('Feed Options');
    this.currentStep = 0;
  }

  getCurrentStepTemplate(): string {
    if (this.wizardSteps[this.currentStep] && this.wizardSteps[this.currentStep].toLowerCase().includes('fmv')) {
      return `
        <label>Video URI</label>
        <input type="url" name="feedUrl" [(ngModel)]="fmvOptions.ipaddr" class="form-control" style="width: 50%" required />
        <span style="color:red" class="fs-6" *ngIf="!fmvOptions.ipaddr">Feed URI is required.</span>
        <br />
        <label>Video Type</label>
        <input list="videotypes" type="text" name="videotype" [(ngModel)]="fmvOptions.videoType" class="form-control" style="width: 50%" required />
        <datalist id="videotypes">
          <option *ngFor="let type of videoTypes" [value]="type"></option>
        </datalist>
        <span style="color:red" class="fs-6" *ngIf="!fmvOptions.videoType">Video type is required.</span>
      `;
    }
    // For other plugin steps, return a placeholder
    if (this.currentStep > 0 && this.currentStep < this.wizardSteps.length - 1) {
      return '<div>Plugin Step Content</div>';
    }
    return '';
  }

  next(): void {
    if (this.currentStep < this.wizardSteps.length - 1) {
      this.currentStep++;
    }
  }

  back(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  canProceed(): boolean {
    if (this.currentStep === 0) {
      return this.wizardForm.valid;
    }
    
    if (this.wizardSteps[this.currentStep] && this.wizardSteps[this.currentStep].toLowerCase().includes('fmv')) {
      return this.fmvOptions.ipaddr.trim() !== '' && this.fmvOptions.videoType.trim() !== '';
    }
    
    return true;
  }
  canFinish(): boolean {
    return this.wizardForm.valid;
  }

  finish(): void {
    const feedName = this.wizardForm.get('feedName')?.value || 'New Feed';
    this.feedWizard.createFeed(
      feedName,
      { autostart: this.autoStartStatus },
      this.fmvOptions
    );
    this.close.emit();
  }

  cancel(): void {
    this.close.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    // Prevent the click from propagating to the overlay
    event.stopPropagation();
  }

  // Optionally, add setOption/getOption for plugin compatibility
  setOption(option: string, value: any) {
    if (option === 'ipaddr') this.fmvOptions.ipaddr = value;
    if (option === 'videoType') this.fmvOptions.videoType = value;
  }
  getOption(option: string) {
    if (option === 'ipaddr') return this.fmvOptions.ipaddr;
    if (option === 'videoType') return this.fmvOptions.videoType;
    return undefined;
  }
}
